"use client"

import { AlertTriangle, ArrowUpRight, Clock3, Megaphone, Phone, UserRoundPlus, X } from "lucide-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"
import { Button } from "@/components/ui/button"

export interface LeadNotificationDetails {
  /** Eyebrow label; defaults to the "New lead" title. */
  heading?: string | null
  phone?: string | null
  source?: string | null
  campaign?: string | null
  receivedAt?: string | null
  /** "danger" = a duplicate re-application: red bar, red icon, so it never reads as a fresh lead. */
  tone?: "default" | "danger"
}

interface LeadNotificationCardProps extends LeadNotificationDetails {
  name: string
  onOpen: () => void
  onDismiss: () => void
}

export function LeadNotificationCard({ name, heading, phone, source, campaign, receivedAt, tone = "default", onOpen, onDismiss }: LeadNotificationCardProps) {
  const danger = tone === "danger"
  const t = useTranslations("notifications.leadPopup")
  const format = useFormatter()
  const received = receivedAt ? new Date(receivedAt) : null
  const receivedLabel = received && Number.isFinite(received.getTime())
    ? format.dateTime(received, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : null

  return (
    <article
      data-lead-notification
      aria-label={t("announcement", { name })}
      className="w-[var(--width)] max-w-[calc(100vw-32px)] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl shadow-black/10"
    >
      <div className={danger ? "h-1 bg-destructive" : "h-1 bg-primary"} />
      <div className="p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className={danger ? "flex size-9 items-center justify-center rounded-xl bg-destructive/10 text-destructive" : "flex size-9 items-center justify-center rounded-xl bg-primary/10 text-primary"}>
              {danger ? <AlertTriangle className="size-4" aria-hidden="true" /> : <UserRoundPlus className="size-4" aria-hidden="true" />}
            </span>
            <span className={danger ? "text-[11px] font-semibold uppercase tracking-wider text-destructive" : "text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"}>{heading?.trim() || t("title")}</span>
          </div>
          <Button variant="ghost" size="icon" className="-mr-1 -mt-1" aria-label={t("dismiss")} onClick={onDismiss}>
            <X className="size-4" aria-hidden="true" />
          </Button>
        </div>

        <h3 className="break-words text-lg font-semibold leading-snug tracking-tight">{name}</h3>
        {phone?.trim() && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <Phone className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="select-text font-mono text-[13px] tabular-nums">{phone}</span>
          </div>
        )}

        {(source?.trim() || campaign?.trim()) && (
          <div className="mt-3 rounded-lg bg-muted/50 px-3 py-2.5">
            {source?.trim() && <p className="text-xs font-medium">{source}</p>}
            {campaign?.trim() && (
              <p className="mt-1 flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground">
                <Megaphone className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                <span className="break-words"><span className="sr-only">{t("campaign")}: </span>{campaign}</span>
              </p>
            )}
          </div>
        )}

        {receivedLabel && (
          <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Clock3 className="size-3 shrink-0" aria-hidden="true" />
            <time dateTime={received!.toISOString()}>{t("received", { time: receivedLabel })}</time>
          </p>
        )}
        <Button variant={danger ? "destructive" : "default"} className="mt-4 h-9 w-full justify-between px-3 text-sm text-white" onClick={onOpen}>
          {t("openLead")}
          <ArrowUpRight className="size-4" aria-hidden="true" />
        </Button>
      </div>
    </article>
  )
}
